import { Bell } from 'lucide-react';
import { Avatar, Spinner } from '@/components/ui';
import { useAuth } from '@/app/providers';
import { useActivityFeed } from '../hooks/useSocial';
import { stripHandle } from '../helpers';
import { FriendRequestsPanel } from './FriendRequestsPanel';

/** Compact relative time, e.g. "3m", "2h", "5d". */
function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff)) return '';
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return d < 7 ? `${d}d` : `${Math.floor(d / 7)}w`;
}

const SECTION_LABEL =
  'mb-3 flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.7px] text-lo';

/**
 * The Notifications surface: community activity (from the viewer's communities /
 * friends, via `activity_feed`) plus incoming friend requests. All live from
 * Supabase. Text-forward — clean divider lists rather than boxed cards — so it
 * reads as a full page rather than a dropdown widget.
 */
export function NotificationsPanel() {
  const { user } = useAuth();
  const { data: activity = [], isLoading } = useActivityFeed(user?.id);

  return (
    <div className="space-y-10">
      <section>
        <div className={SECTION_LABEL}>
          <Bell size={13} strokeWidth={2.4} />
          Community activity
        </div>

        {isLoading ? (
          <div className="flex h-16 items-center">
            <Spinner />
          </div>
        ) : activity.length === 0 ? (
          <p className="text-sm leading-relaxed text-lo">
            New activity from your communities and friends will show up here.
          </p>
        ) : (
          <ul className="divide-y divide-white/[0.06] border-y border-white/[0.06]">
            {activity.map((item) => {
              const name =
                item.actor?.display_name || stripHandle(item.actor?.handle) || 'Someone';
              return (
                <li key={item.id} className="flex items-start gap-3 py-3.5">
                  <Avatar
                    src={item.actor?.avatar_url}
                    name={name}
                    size={36}
                    className="mt-0.5 flex-none"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm leading-snug text-mid">
                      <span className="font-bold text-hi">{name}</span> {item.action_text}
                      {item.community && (
                        <span className="font-semibold text-white/70"> · {item.community.name}</span>
                      )}
                    </p>
                    <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.4px] text-lo">
                      {timeAgo(item.created_at)}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <FriendRequestsPanel />
    </div>
  );
}
